import { NotifyHelper } from 'classes/helpers/notify.helper';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { MachineServiceLogsRoutes } from 'lib_ts/enums/routing/routes/machine-service-logs';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IMachineServiceLog } from 'lib_ts/interfaces/i-machine-service-log';
import { BaseRESTService } from 'services/_base-rest.service';

const LIMIT_LATEST_LOGS = 200;

export class MachineServiceLogsService extends BaseRESTService {
  private static instance: MachineServiceLogsService;
  static getInstance(): MachineServiceLogsService {
    if (!MachineServiceLogsService.instance) {
      MachineServiceLogsService.instance = new MachineServiceLogsService();
    }

    return MachineServiceLogsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.MachineServiceLogs,
    });
  }

  async getLatest(
    machineID: string
  ): Promise<IMachineServiceLog[] | undefined> {
    return await this.get({
      uri: `${MachineServiceLogsRoutes.GetLatest}/${machineID}/${LIMIT_LATEST_LOGS}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while fetching latest service logs.',
          });
          return undefined;
        }

        return result.data as IMachineServiceLog[];
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while fetching latest service logs.',
        });
        return undefined;
      });
  }

  async getAll(machineID: string): Promise<IMachineServiceLog[]> {
    return await this.get({
      uri: `${MachineServiceLogsRoutes.GetAll}/${machineID}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while fetching latest service logs.',
          });
          return [];
        }

        return result.data as IMachineServiceLog[];
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while fetching latest service logs.',
        });
        return [];
      });
  }

  async createOne(
    payload: Partial<IMachineServiceLog>
  ): Promise<IMachineServiceLog | undefined> {
    return await this.post(
      {
        uri: MachineServiceLogsRoutes.CreateOne,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while creating a service log.',
          });
          return undefined;
        }

        return result.data as IMachineServiceLog;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while creating a service log.',
        });
        return undefined;
      });
  }

  async deleteOne(id: string): Promise<boolean> {
    return await this.delete({
      uri: `${MachineServiceLogsRoutes.DeleteOne}/${id}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while deleting a service log.',
          });
          return false;
        }

        return true;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while deleting a service log.',
        });
        return false;
      });
  }

  async updateOne(
    id: string,
    payload: Partial<IMachineServiceLog>
  ): Promise<IMachineServiceLog | undefined> {
    return await this.put(
      {
        uri: `${MachineServiceLogsRoutes.UpdateOne}/${id}`,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while updating a service log.',
          });
          return undefined;
        }

        return result.data as IMachineServiceLog;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while updating a service log.',
        });
        return undefined;
      });
  }
}
